import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  createSetting,
  deleteSetting,
  getSetting,
  listSettings,
  updateSetting,
} from "./settingsService";

const UNITS = [
  "EACH", "SERVICE", "TRIP", "KILOMETRE", "METRE",
  "HOUR", "DAY", "NIGHT", "MONTH", "BOX", "PACK",
];

const empty = {
  name: "",
  alternative_name: "",
  item_type: "NON STOCK ITEM",
  category: "",
  department: "",
  barcode: "",
  unit_of_measure: "EACH",
  purchase_uom: "",
  purchase_quantity: "1",
  pricing_type: "NORMAL",
  unit_price: "0.00",
  unit_cost: "0.00",
  tax_code: "",
  range_boundary: "0",
  additional_price: "0.00",
  quantity_on_hand: "0",
  reorder_level: "0",
  reorder_quantity: "0",
  minimum_quantity: "0",
  maximum_quantity: "0",
  supplier_code: "",
  manufacturer_code: "",
  is_active: true,
};

export default function ItemSetup() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editorTab, setEditorTab] = useState("general");
  const [search, setSearch] = useState("");
  const [alternativeItem, setAlternativeItem] = useState("");
  const [locationForm, setLocationForm] = useState({
    location: "",
    quantity_on_hand: "0",
  });

  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      listSettings("items"),
      listSettings("item-categories"),
      listSettings("item-departments"),
      listSettings("item-locations"),
    ]);
    setItems(a);
    setCategories(b.filter((x) => x.is_active));
    setDepartments(c.filter((x) => x.is_active));
    setLocations(d.filter((x) => x.is_active));
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load Item Management."));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    return items.filter((x) =>
      `${x.code} ${x.name} ${x.category_name} ${x.department_name}`
        .toUpperCase()
        .includes(q)
    );
  }, [items, search]);

  const view = async (item) => {
    const full = await getSetting("items", item.id);
    setSelected(full);
    setForm(full);
  };

  const startNew = () => {
    setSelected(null);
    setForm(empty);
    setEditorTab("general");
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    try {
      const saved = selected
        ? await updateSetting("items", selected.id, form)
        : await createSetting("items", form);

      await load();
      await view(saved);
      setOpen(false);
      toast.success("Item saved.");
    } catch (error) {
      toast.error(
        Object.values(error.response?.data || {}).flat().join(" ") ||
          "Could not save item."
      );
    }
  };

  const addAlternative = async () => {
    if (!selected || !alternativeItem) return;
    await createSetting("item-alternatives", {
      item: selected.id,
      alternative_item: alternativeItem,
    });
    await view(selected);
    setAlternativeItem("");
  };

  const addLocationBalance = async () => {
    if (!selected || !locationForm.location) return;
    await createSetting("item-location-balances", {
      item: selected.id,
      ...locationForm,
    });
    await view(selected);
    setLocationForm({ location: "", quantity_on_hand: "0" });
  };

  const previewQuantity = Number(form.range_boundary || 0) + 2;
  const previewTotal =
    form.pricing_type === "RANGED"
      ? Number(form.unit_price || 0) +
        Math.max(0, previewQuantity - Number(form.range_boundary || 0)) *
          Number(form.additional_price || 0)
      : previewQuantity * Number(form.unit_price || 0);

  return (
    <section className="settings-panel modern-setup-panel item-master-panel">
      <div className="settings-panel__head">
        <div>
          <h2>Item master</h2>
          <p>
            Maintain products and chargeable services used by plans,
            claims, billing and point of sale.
          </p>
        </div>
        <button className="settings-primary-action" onClick={startNew}>
          <i className="bi bi-plus-lg"></i>
          New item
        </button>
      </div>

      <div className="setup-toolbar">
        <div className="setup-search">
          <i className="bi bi-search"></i>
          <input
            value={search}
            placeholder="Search by item code, name, category or department"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="setup-record-count">{filtered.length} items</span>
      </div>

      <div className="settings-table-wrap setup-main-table">
        <table>
          <thead>
            <tr>
              <th>Product code</th>
              <th>Description</th>
              <th>Type</th>
              <th>Pricing</th>
              <th>Unit price</th>
              <th>Stock</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => (
              <tr
                key={item.id}
                className={selected?.id === item.id ? "selected-row" : ""}
                onClick={() => view(item)}
              >
                <td><span className="record-code">{item.code}</span></td>
                <td><strong>{item.name}</strong></td>
                <td>{item.item_type}</td>
                <td>{item.pricing_type}</td>
                <td>{item.unit_price}</td>
                <td>{item.item_type === "STOCK ITEM" ? item.quantity_on_hand : "SERVICE"}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button className="settings-icon-action" onClick={async () => { await view(item); setEditorTab("general"); setOpen(true); }} title="Edit">
                    <i className="bi bi-pencil-square"></i>
                  </button>
                  <button className="settings-icon-action danger" onClick={async () => {
                    if (window.confirm("Delete this item?")) {
                      try {
                        await deleteSetting("items", item.id);
                        await load();
                      } catch {
                        toast.error("This item is already in use.");
                      }
                    }
                  }} title="Delete">
                    <i className="bi bi-trash3"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="record-detail-workspace">
          <div className="record-detail-header">
            <div>
              <span className="record-code">{selected.code}</span>
              <h3>{selected.name}</h3>
              <p>{selected.item_type} · {selected.unit_of_measure || "NO UNIT"} · {selected.pricing_type}</p>
            </div>
            <button className="secondary-action" onClick={() => { setEditorTab("general"); setOpen(true); }}>
              <i className="bi bi-pencil-square"></i>
              Edit item
            </button>
          </div>

          <div className="plan-summary-grid">
            <Summary icon="currency-dollar" label="Unit price" value={selected.unit_price} />
            <Summary icon="calculator" label="Weighted average cost" value={selected.weighted_average_cost} />
            <Summary icon="clock-history" label="Last cost" value={selected.last_cost} />
            <Summary icon="boxes" label="Quantity on hand" value={selected.quantity_on_hand} />
          </div>

          {selected.pricing_type === "RANGED" && (
            <div className="service-pricing-banner">
              <i className="bi bi-signpost-split"></i>
              <div>
                <strong>Ranged service pricing</strong>
                <p>{selected.pricing_explanation}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="setup-modal-backdrop" onMouseDown={() => setOpen(false)}>
          <div className="setup-modal item-editor-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="setup-modal__header">
              <div>
                <span>{selected?.code || "NEW ITEM"}</span>
                <h3>{selected ? "Edit item" : "Create item"}</h3>
              </div>
              <button className="modal-close" onClick={() => setOpen(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="item-editor-tabs">
              {[
                ["general", "General details"],
                ["pricing", "Pricing & service rules"],
                ["stock", "Stock & locations"],
                ["purchasing", "Purchasing"],
                ["alternatives", "Alternative items"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={editorTab === id ? "active" : ""}
                  onClick={() => setEditorTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            <form className="modern-editor-form" onSubmit={save}>
              {editorTab === "general" && (
                <>
                  <Field label="Product description" wide>
                    <input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })} required />
                  </Field>
                  <Field label="Alternative name" wide>
                    <input value={form.alternative_name || ""} onChange={(e) => setForm({ ...form, alternative_name: e.target.value.toUpperCase() })} />
                  </Field>
                  <Field label="Item type">
                    <select value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })}>
                      <option>NON STOCK ITEM</option>
                      <option>STOCK ITEM</option>
                    </select>
                  </Field>
                  <Field label="Barcode">
                    <input value={form.barcode || ""} onChange={(e) => setForm({ ...form, barcode: e.target.value.toUpperCase() })} />
                  </Field>
                  <Field label="Category">
                    <select value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} required>
                      <option value="">SELECT...</option>
                      {categories.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Department">
                    <select value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} required>
                      <option value="">SELECT...</option>
                      {departments.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Unit of measure">
                    <select value={form.unit_of_measure || ""} onChange={(e) => setForm({ ...form, unit_of_measure: e.target.value })}>
                      <option value="">SELECT...</option>
                      {UNITS.map((u) => <option key={u}>{u}</option>)}
                    </select>
                  </Field>
                  <Field label="Tax code">
                    <input value={form.tax_code || ""} onChange={(e) => setForm({ ...form, tax_code: e.target.value.toUpperCase() })} />
                  </Field>
                </>
              )}

              {editorTab === "pricing" && (
                <>
                  <Field label="Pricing type">
                    <select value={form.pricing_type} onChange={(e) => setForm({ ...form, pricing_type: e.target.value })}>
                      <option>NORMAL</option>
                      <option>RANGED</option>
                    </select>
                  </Field>
                  <Field label="Unit price">
                    <input type="number" step="0.01" min="0" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} />
                  </Field>
                  <Field label="Unit cost">
                    <input type="number" step="0.01" min="0" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
                  </Field>
                  <Field label="Weighted average cost">
                    <input value={form.weighted_average_cost || "0.00"} disabled />
                  </Field>
                  <Field label="Last cost">
                    <input value={form.last_cost || "0.00"} disabled />
                  </Field>
                  {form.pricing_type === "RANGED" && (
                    <>
                      <Field label={`Base range (${form.unit_of_measure || "UNITS"})`}>
                        <input type="number" step="0.01" min="0.01" value={form.range_boundary} onChange={(e) => setForm({ ...form, range_boundary: e.target.value })} />
                      </Field>
                      <Field label={`Additional price per ${form.unit_of_measure || "UNIT"}`}>
                        <input type="number" step="0.01" min="0" value={form.additional_price} onChange={(e) => setForm({ ...form, additional_price: e.target.value })} />
                      </Field>
                      <div className="pricing-preview editor-wide">
                        <strong>Pricing preview</strong>
                        <p>
                          Base price covers the first {form.range_boundary || 0} {form.unit_of_measure || "UNITS"}.
                          Each additional {form.unit_of_measure || "UNIT"} costs {form.additional_price || 0}.
                        </p>
                        <span>
                          Example: {previewQuantity} {form.unit_of_measure || "UNITS"} = {previewTotal.toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </>
              )}

              {editorTab === "stock" && (
                <>
                  {form.item_type !== "STOCK ITEM" ? (
                    <div className="editor-wide settings-empty">
                      Stock controls apply only to STOCK ITEM records.
                    </div>
                  ) : (
                    <>
                      {["quantity_on_hand", "reorder_level", "reorder_quantity", "minimum_quantity", "maximum_quantity"].map((name) => (
                        <Field key={name} label={name.replaceAll("_", " ")}>
                          <input type="number" step="0.01" value={form[name] || 0} onChange={(e) => setForm({ ...form, [name]: e.target.value })} />
                        </Field>
                      ))}
                      {selected && (
                        <div className="editor-wide linked-editor-section">
                          <h4>Balances by location</h4>
                          <div className="inline-link-form">
                            <select value={locationForm.location} onChange={(e) => setLocationForm({ ...locationForm, location: e.target.value })}>
                              <option value="">SELECT LOCATION...</option>
                              {locations.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                            </select>
                            <input type="number" step="0.01" value={locationForm.quantity_on_hand} onChange={(e) => setLocationForm({ ...locationForm, quantity_on_hand: e.target.value })} />
                            <button type="button" onClick={addLocationBalance}>Add</button>
                          </div>
                          <div className="mini-record-list">
                            {(selected.location_balances || []).map((x) => (
                              <div key={x.id}><span>{x.location_code} · {x.location_name}</span><strong>{x.quantity_on_hand}</strong></div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {editorTab === "purchasing" && (
                <>
                  <Field label="Purchase unit of measure">
                    <select value={form.purchase_uom || ""} onChange={(e) => setForm({ ...form, purchase_uom: e.target.value })}>
                      <option value="">SELECT...</option>
                      {UNITS.map((u) => <option key={u}>{u}</option>)}
                    </select>
                  </Field>
                  <Field label="Purchase quantity">
                    <input type="number" step="0.01" min="0" value={form.purchase_quantity || 1} onChange={(e) => setForm({ ...form, purchase_quantity: e.target.value })} />
                  </Field>
                  <Field label="Last supplier code">
                    <input value={form.supplier_code || ""} onChange={(e) => setForm({ ...form, supplier_code: e.target.value.toUpperCase() })} />
                  </Field>
                  <Field label="Manufacturer code">
                    <input value={form.manufacturer_code || ""} onChange={(e) => setForm({ ...form, manufacturer_code: e.target.value.toUpperCase() })} />
                  </Field>
                </>
              )}

              {editorTab === "alternatives" && (
                <div className="editor-wide linked-editor-section">
                  {!selected ? (
                    <p>Save the item first before linking alternatives.</p>
                  ) : (
                    <>
                      <div className="inline-link-form">
                        <select value={alternativeItem} onChange={(e) => setAlternativeItem(e.target.value)}>
                          <option value="">SELECT ALTERNATIVE ITEM...</option>
                          {items.filter((x) => x.id !== selected.id).map((x) => (
                            <option key={x.id} value={x.id}>{x.code} · {x.name}</option>
                          ))}
                        </select>
                        <button type="button" onClick={addAlternative}>Add alternative</button>
                      </div>
                      <div className="mini-record-list">
                        {(selected.alternatives || []).map((x) => (
                          <div key={x.id}>
                            <span>{x.alternative_code} · {x.alternative_name}</span>
                            <button type="button" className="settings-icon-action danger" onClick={async () => { await deleteSetting("item-alternatives", x.id); await view(selected); }}>
                              <i className="bi bi-trash3"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="setup-modal__actions">
                <button type="button" className="secondary-action" onClick={() => setOpen(false)}>Cancel</button>
                <button type="submit"><i className="bi bi-check2-circle"></i>{selected ? "Update item" : "Save item"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, children, wide = false }) {
  return (
    <label className={wide ? "editor-wide" : ""}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function Summary({ icon, label, value }) {
  return (
    <div className="summary-card">
      <i className={`bi bi-${icon}`}></i>
      <div><span>{label}</span><strong>{value ?? "—"}</strong></div>
    </div>
  );
}
